import styled, { css } from 'styled-components';
import Caret from '@components/svg-icons/caret';
import { generateScales } from '@helpers/generate-media-queries';
import Text from '@components/text';
import { underlineTransition } from '@components/link/link.styled';

export const DropdownContainer = styled.div`
  position: relative;
`;

export const StyledCaret = styled(Caret)`
  ${({ theme }) => css`
    transition: transform 0.25s ease-in-out;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      display: none;
    }
  `}
`;

export const GroupCaret = styled(Caret)`
  ${({ theme }) => css`
    transition: transform 0.25s ease-in-out;
    height: 0.675rem;
    width: 0.675rem;
    transform: scaleY(1);

    path {
      fill: ${theme.colors.primary};
    }
  `}
`;

interface StyledDropDownProps {
  isVisible?: boolean;
}

export const StyledDropDown = styled.div<StyledDropDownProps>`
  ${({ theme, isVisible }) => css`
    display: flex;
    row-gap: 2.5rem;
    column-gap: 2.5rem;
    position: fixed;
    top: 64px;
    padding: 1rem 0.75rem;
    background-color: ${theme.colors.white}EE;
    backdrop-filter: blur(1rem);
    border: 1px solid ${theme.colors.light};
    border-radius: ${theme.borderRadius.normal};
    box-shadow: ${theme.shadows.small};
    visibility: hidden;

    @media only screen and (max-width: ${theme.breakpoints.xl}) {
      column-gap: 2rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      column-gap: 1.75rem;
      display: none;
    }

    @keyframes drop {
      from {
        opacity: 0;
        transform: translateY(-10px);
        visibility: visible;
      }

      to {
        opacity: 1;
        transform: translateY(0);
        visibility: visible;
      }
    }

    ${isVisible &&
    css`
      visibility: visible;
      opacity: 1;
      animation: 0.25s drop ease-in-out;

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        width: 100vw;
        justify-content: center;
        top: 5rem !important;
        left: 0 !important;
      }
    `};
  `}
`;

interface ButtonProps {
  isVisible?: boolean;
}

export const Button = styled.button<ButtonProps>`
  ${({ theme, isVisible }) => {
    return css`
      display: flex;
      column-gap: 0.25rem;
      align-items: center;
      font-family: ${theme.fontFamily.text};
      font-size: 1rem;
      font-weight: 500;
      color: ${theme.colors.black};
      cursor: pointer;
      background: none;
      border: none;
      margin: 0;
      padding: 0;

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        font-size: 1rem;
      }

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        display: none;
      }

      span {
        ${underlineTransition}
        background-size: 0% 0.125rem;

        :hover,
        :focus {
          background-size: 100% 0.125rem;
        }
      }

      ${isVisible &&
      css`
        span {
          background-size: 100% 0.125rem;
        }

        ${StyledCaret} {
          transform: scaleY(-1);
        }
      `};
    `;
  }}
`;

export const MobileButton = styled.button<ButtonProps>`
  ${({ theme, isVisible }) => {
    return css`
      display: none;
      column-gap: 0.25rem;
      align-items: center;
      font-family: ${theme.fontFamily.text};
      font-size: 1.0625rem;
      font-weight: 500;
      color: ${theme.colors.primary};
      cursor: pointer;
      background: none;
      border: none;
      margin: 0;
      padding: 0;

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        cursor: default;
        font-size: 1.125rem;
        margin-bottom: 1rem;
        display: flex;
      }

      ${isVisible &&
      css`
        ${GroupCaret} {
          transform: scaleY(-1);
        }
      `};
    `;
  }}
`;

export const Links = styled.a`
  ${({ theme }) => css`
    width: 100%;
    color: ${theme.colors.black};
    text-decoration: none;
    font-family: ${theme.fontFamily.text};
    font-size: 1rem;
    line-height: ${theme.lineHeights.base};
    font-weight: 400;
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 0.5rem;
    border-radius: ${theme.borderRadius.normal};

    &:hover {
      background-color: ${theme.colors.primary};
      color: ${theme.colors.white};
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      font-size: 1rem;
    }
  `}
`;

export const AccentLink = styled(Links)`
  ${({ theme }) => css`
    font-size: 1rem;
    color: ${theme.colors.secondary};
    margin-top: 0.375rem;
    display: inline-flex;
    width: fit-content;
  `}
`;

interface TriangleProps {
  isVisible?: boolean;
}

export const Triangle = styled.div<TriangleProps>`
  ${({ theme, isVisible }) => css`
    position: absolute;
    z-index: 1;
    visibility: ${isVisible ? 'visible' : 'hidden'};
    top: -0.3725rem;
    left: 0;
    right: 0;
    margin-left: auto;
    margin-right: auto;
    width: 0;
    height: 0;
    border-left: 0.5rem solid ${theme.colors.transparent};
    border-right: 0.5rem solid ${theme.colors.transparent};
    border-bottom: 0.375rem solid ${theme.colors.light};
  `}
`;

export const Title = styled(Text)`
  ${({ theme }) => css`
    font-family: ${theme.fontFamily.additional};
    font-size: 0.875rem;
    line-height: ${theme.lineHeights.sm};
    color: ${theme.colors.primaryLight};
    text-transform: uppercase;
    font-weight: bold;
    margin-bottom: 0.5rem;
  `}
`;

interface DropDownColumnProps {
  $splitColumns?: boolean;
}

export const DropDownColumn = styled.div<DropDownColumnProps>`
  ${({ theme, $splitColumns }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 0.25rem;
    max-width: ${$splitColumns ? '40rem' : '20rem'};

    .columns-container {
      display: flex;
      gap: 1.5rem;
    }

    .column {
      display: flex;
      flex-direction: column;
      row-gap: 0.25rem;
      min-width: 10rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      row-gap: 1rem;

      .columns-container {
        flex-direction: column;
        gap: 0;
      }

      .column {
        width: 100%;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      width: 100%;
      max-width: unset;
    }
  `}
`;

interface LinkContainerProps {
  $splitColumns?: boolean;
}

export const LinkContainer = styled.div<LinkContainerProps>`
  min-width: ${({ $splitColumns }) => ($splitColumns ? '18rem' : '10rem')};
  max-width: ${({ $splitColumns }) => ($splitColumns ? '18rem' : '10rem')};

  :last-child {
    margin-bottom: 0;
  }
`;

export const Description = styled(Title)`
  ${({ theme }) => css`
    margin: 0.25rem 0;
    text-transform: unset;
    color: ${theme.colors.dark};
    font-weight: normal;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      display: none;
    }
  `}
`;

export const MobileMenu = styled.div`
  ${({ theme }) => css`
    display: none;
    position: fixed;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      position: unset;
      display: grid;
      grid-template-columns: 1fr;
      row-gap: 2.5rem;
      ${generateScales('padding-bottom', '1rem', '2rem', theme)}
      ${generateScales('padding-left', '1rem', '2rem', theme)}
      margin-top: 1rem;

      ${Title}, ${Description}, ${Links} {
        color: ${theme.colors.black};
      }

      ${Title}, ${Description} {
        opacity: 0.75;
      }

      ${LinkContainer} {
        padding-left: 1rem;
      }
    }
  `}
`;
